import * as React from 'react';
import { useEffect } from 'react';

import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import tinytime from 'tinytime';

import { Button, Text } from '@components/common';
import { WindowHeader, WindowLayout, WindowLayoutBody } from '@components/layouts/WindowLayout';
import { renderHighlightContent } from '@components/RepositoryApp/components/PreviewContentSlideout/components/HighlightContent';
import { CalendarSVG, PersonSVG, PublicAssetLogoSVG } from '@components/svgs';
import { track } from '@components/tracking';

import { api } from '../api';
import { VideoPage } from '../components/VideoPage';

const template = tinytime('{Do} {MMMM} {YYYY}');

export const HighlightPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { token } = useParams() as { token: string };

  const { data, isLoading, isSuccess } = api.useGetHighlightQuery({ uuid: token });

  const clip = data?.clip;

  useEffect(() => {
    if (isSuccess && clip) {
      track('viewed_clip', {
        layout: 'public',
        artifact_type: 'clip',
        session_uuid: clip.session_uuid,
        highlight_uuid: clip?.highlight_uuid,
        has_recording: !!clip?.playback_urls,
        is_creator: false,
        is_guest: true
      });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccess && data && !clip) {
      track('viewed_non_video_highlight', {
        layout: 'public',
        artifact_type: 'highlight',
        uuid: data.uuid,
        is_creator: false,
        is_guest: true
      });
    }
  }, [isSuccess]);

  if (clip) {
    return (
      <VideoPage
        isLoading={isLoading}
        title={clip.title}
        date={clip?.session_at && (new Date(clip.session_at) as any)}
        name={clip?.public_display_names ? clip?.public_display_names : undefined}
        playbackUrls={clip?.playback_urls}
        clip={clip}
        signUpUrl={`/users/sign_up?asset_referrer=${token}`}
        renderSpeakerSection={() => (
          <section>
            {clip?.speaker && (
              <Text className='mb-6 mt-6' bold>
                {clip.speaker}
              </Text>
            )}
            {clip?.highlight_text && <Text>{clip.highlight_text}</Text>}
          </section>
        )}
      />
    );
  }

  return (
    <WindowLayout>
      <WindowHeader>
        <a href='/'>
          <PublicAssetLogoSVG />
        </a>
        <div className='ml-auto flex'>
          <Button link href='/users/sign_in'>
            Log in
          </Button>
          <Button primary href={`/users/sign_up?asset_referrer=${token}`}>
            Sign up for free
          </Button>
        </div>
      </WindowHeader>
      <WindowLayoutBody className='h-full bg-white'>
        <div className='px-page py-6'>
          <section>
            {isLoading && <Skeleton className='mb-6 h-8 max-w-sm' />}
            <div className='mb-3 space-y-2'>
              {data?.title && <Text h='700'>{data.title}</Text>}
              {data?.description && <Text mb='4'>{data.description}</Text>}
            </div>
            {!isLoading && (
              <div className='mb-6 flex space-x-4'>
                {data?.created_at && (
                  <div className='flex items-center space-x-2'>
                    <CalendarSVG />
                    <Text>{template.render(new Date(data.created_at))}</Text>
                  </div>
                )}
                {data?.creator.name && (
                  <div className='flex items-center space-x-2'>
                    <PersonSVG />
                    <Text>{data?.creator.name}</Text>
                  </div>
                )}
              </div>
            )}
          </section>
          {data && (
            <div className='mx-auto max-w-3xl'>{!!data.content?.length && renderHighlightContent(data.content)}</div>
          )}
        </div>
      </WindowLayoutBody>
    </WindowLayout>
  );
};
